import React, { useState } from 'react'
import {
  makeStyles,
  createStyles,
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  InputLabel,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@material-ui/core/'
import { Formik } from 'formik'
import { omit } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { getBackPassword } from 'src/store/slices/authSlice'
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined'
import VisibilityOffOutlined from '@material-ui/icons/VisibilityOffOutlined'
import { API } from '../../../store/api'
import { DIALING_CODES } from 'src/utils/constant'
import CodeButton from 'src/mobile/components/CodeButton'
import { oriSchema } from 'src/utils/schema'
import { useTranslateFormErrors } from 'src/hooks'
import FocusError from 'src/mobile/components/FocusError'
import AuthHeader from 'src/mobile/components/Auth/AuthHeader'
import AuthTabs from 'src/mobile/components/Auth/AuthTabs'
import DialingCodeSelector from 'src/mobile/components/DialingCodeSelector'
// import PopperHelperText from 'src/mobile/components/PopperHelperText'
import { toast } from 'react-toastify'
const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      minHeight: `calc(100vh - ${theme.spacing(41)}px)`,
    },
    main: {
      padding: theme.spacing(0, 2.5),
      background: '#fff',
    },
    popperHelperText: {
      color: '#9CA3AF',
    },
    title: {
      fontWeight: 600,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2),
      fontSize: 26,
      [theme.breakpoints.down('xs')]: {
        fontSize: 23,
      },
    },
    tips: {
      fontWeight: 400,
      marginBottom: theme.spacing(2),
      fontSize: 16,
    },
    inputLabel: {
      fontWeight: 400,
      color: '#111827',
      fontSize: 14,
      marginTop: 5,
    },
    helperText: {
      marginLeft: 0,
      marginBottom: theme.spacing(0.5),
    },
    submit: {
      height: 56,
    },
  }),
)

const initialValues = {
  verificationType: 'phone',
  email: '',
  phone: '',
  dialingCode: DIALING_CODES[0].value,
  code: '',
  newPassword: '',
  confirmPassword: '',
}

const ForgotPassword = ({ source }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation()
  const { getBackPasswordStatus } = useSelector((state) => state.auth)
  const [signupStep, setSignupStep] = useState(1)
  const [tabvalue, setTabvalue] = useState('phone')
  const [showPassword, setShowPassword] = useState(false)
  const handleClickShowPassword = () => setShowPassword((show) => !show)
  const theme = useTheme()
  const location = useLocation()
  const { pathname } = location
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const schema = oriSchema(t).pick([
    'verificationType',
    'email',
    'dialingCode',
    'phone',
    'code',
    'newPassword',
    'confirmPassword',
  ])

  const handleSubmit = async (values) => {
    if (getBackPasswordStatus === 'pending') return
    const omitKeys =
      values.verificationType === 'email'
        ? ['verificationType', 'phone', 'dialingCode', 'confirmPassword', 'code']
        : ['verificationType', 'email', 'confirmPassword', 'code']
    const pureValues = omit(values, omitKeys)
    await dispatch(getBackPassword(pureValues)).unwrap()
    toast.success(t('toast.change_password_success'))
    history.replace(pathname === '/add-order/forgot-password' ? '/add-order/signin' : '/signin')
  }

  const handleValidateCode = async (values) => {
    const data =
      values.verificationType === 'phone'
        ? { phone: values.phone, code: values.code, dialingCode: values.dialingCode }
        : { email: values.email, code: values.code }
    await API.codeVerify(data)
    setSignupStep(2)
  }

  return (
    <Box width="100%" maxWidth={630} margin="0 auto" className={classes.root}>
      {source !== 'order' && (
        <AuthHeader
          title={isMobile ? t('common.set_new_pwd') : ''}
          goStepOne={() => {
            setSignupStep(1)
          }}
          step={signupStep}
          showLogo
        />
      )}

      <Box className={classes.main}>
        {!isMobile && signupStep === 1 ? (
          <Box>
            <Typography variant="h2" className={classes.title}>
              {t('are_you_sure.forget_your_password')}
            </Typography>
            <Typography className={classes.tips} variant="h5">
              {tabvalue === 'phone' ? t('common.reset_pwd_tip') : t('common.reset_pwd_tip_email')}
            </Typography>
          </Box>
        ) : (
          ''
        )}
        <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit}>
          {(props) => {
            const { values, setFieldTouched, setFieldValue, handleSubmit, handleChange, errors, touched } =
              props

            const onBeforeGetCode = async () => await schema.validateAt(values.verificationType, values)

            const onGetCodeError = () => setFieldTouched(values.verificationType)

            const isError = (field) => touched[field] && Boolean(errors[field])
            const errorText = (field) => touched[field] && errors[field]
            useTranslateFormErrors(errors, touched, setFieldTouched)

            const handleTabsChange = (value) => {
              value && setFieldValue('verificationType', value)
              setTabvalue(value)
            }

            return (
              <form onSubmit={handleSubmit}>
                {signupStep === 1 && (
                  <AuthTabs
                    onChange={handleTabsChange}
                    activeTab={values.verificationType}
                    hideLogo
                    authType={t('common.verification')}
                  />
                )}
                {signupStep === 1 ? (
                  <>
                    {isMobile ? (
                      <Box>
                        <Typography variant="h2" className={classes.title}>
                          {t('are_you_sure.forget_your_password')}
                        </Typography>
                        {values.verificationType === 'phone' ? (
                          <Typography className={classes.tips} variant="h5">
                            {t('common.reset_pwd_tip')}
                          </Typography>
                        ) : (
                          <Typography className={classes.tips} variant="h5">
                            {t('common.reset_pwd_tip_email')}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      ''
                    )}

                    {values.verificationType === 'phone' ? (
                      <>
                        <InputLabel classes={{ root: classes.inputLabel }} shrink>
                          {t('form.mobile_phone')}
                        </InputLabel>
                        <TextField
                          margin="normal"
                          fullWidth
                          id="phone"
                          name="phone"
                          variant="outlined"
                          value={values.phone}
                          onChange={handleChange}
                          error={isError('phone')}
                          helperText={errorText('phone')}
                          FormHelperTextProps={{
                            classes: {
                              root: classes.helperText,
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <DialingCodeSelector
                                value={values.dialingCode}
                                onChange={(dialingCode) => setFieldValue('dialingCode', dialingCode)}
                              />
                            ),
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <InputLabel classes={{ root: classes.inputLabel }} shrink>
                          {t('common.email_address')}
                        </InputLabel>
                        <TextField
                          margin="normal"
                          fullWidth
                          id="email"
                          name="email"
                          variant="outlined"
                          placeholder={t('validation.please_enter', { field: `$t(form.email)` })}
                          value={values.email}
                          onChange={handleChange}
                          error={isError('email')}
                          helperText={errorText('email')}
                          FormHelperTextProps={{
                            classes: {
                              root: classes.helperText,
                            },
                          }}
                        />
                      </>
                    )}
                    <CodeButton
                      type={values.verificationType}
                      action="forget_password"
                      params={
                        values.verificationType === 'email'
                          ? { [values.verificationType]: values[values.verificationType] }
                          : {
                              dialingCode: values.dialingCode,
                              [values.verificationType]: values[values.verificationType],
                            }
                      }
                      onBeforeGetCode={onBeforeGetCode}
                      onGetCodeError={onGetCodeError}
                    />
                    <InputLabel classes={{ root: classes.inputLabel }} shrink>
                      {t('form.input_code')}
                    </InputLabel>
                    <TextField
                      margin="normal"
                      fullWidth
                      id="code"
                      name="code"
                      variant="outlined"
                      value={values.code}
                      placeholder={t('validation.please_enter', { field: `$t(form.v_code)` })}
                      onChange={handleChange}
                      error={isError('code')}
                      helperText={errorText('code')}
                      FormHelperTextProps={{
                        classes: {
                          root: classes.helperText,
                        },
                      }}
                    />
                  </>
                ) : (
                  <Box mt={source === 'order' ? 0 : 3} pt={source === 'order' ? 2 : 0}>
                    <InputLabel classes={{ root: classes.inputLabel }}>{t('form.new_password')}</InputLabel>
                    <TextField
                      margin="normal"
                      fullWidth
                      name="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      id="new-password"
                      placeholder={t('validation.please_enter', { field: `$t(form.new_password)` })}
                      value={values.newPassword}
                      onChange={handleChange}
                      error={isError('newPassword')}
                      helperText={errorText('newPassword')}
                      FormHelperTextProps={{
                        classes: {
                          root: classes.helperText,
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              edge="end"
                            >
                              {!showPassword ? (
                                <VisibilityOffOutlined color="primary" />
                              ) : (
                                <VisibilityOutlined color="primary" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography variant="caption" className={classes.popperHelperText}>
                      {t('validation.password_match', { field: '$t(form.password)' })}
                    </Typography>
                    <Box mt={2}>
                      <InputLabel classes={{ root: classes.inputLabel }}>
                        {t('form.confirm_new_password')}
                      </InputLabel>
                      <TextField
                        margin="normal"
                        fullWidth
                        name="confirmPassword"
                        variant="outlined"
                        type={showPassword ? 'text' : 'password'}
                        id="confirm-password"
                        placeholder={t('validation.please_enter', { field: `$t(form.confirm_password)` })}
                        value={values.confirmPassword}
                        onChange={handleChange}
                        error={isError('confirmPassword')}
                        helperText={errorText('confirmPassword')}
                        FormHelperTextProps={{
                          classes: {
                            root: classes.helperText,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                )}

                <Box mt={3}>
                  {signupStep === 1 ? (
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      disabled={!values.code}
                      className={classes.submit}
                      onClick={() => {
                        handleValidateCode(values)
                      }}
                    >
                      {t('common.go_on')}
                    </Button>
                  ) : (
                    <Button fullWidth type="submit" variant="contained" color="secondary">
                      {getBackPasswordStatus === 'pending' ? (
                        <CircularProgress color="inherit" size={24} />
                      ) : (
                        t('common.save')
                      )}
                    </Button>
                  )}
                </Box>
                <FocusError />
              </form>
            )
          }}
        </Formik>
      </Box>
    </Box>
  )
}

export default ForgotPassword
